/**
 * Setup Wizard v2 — "Load sample data?" step (design §4.4-4.5).
 *
 * Fetches the onboarding demo catalog for the tenant's stated industry and
 * pre-selects the recommended variation (overridable). The actual provisioning
 * runs AFTER the tenant is created (the wizard owns that), so this step only
 * collects the choice: a variation to load, or "start empty".
 *
 * Until a variation's golden tenant is authored it is listed but not selectable
 * ("Coming soon"); when none are available the step pre-selects "start empty".
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, PackageOpen, Sparkles } from 'lucide-react';
import { Badge } from '../../../components/ui/badge';
import { cn } from '../../../lib/utils';
import { setupWizardApi, type SetupDemoVariation } from '../../../shared/api/setupWizardApi';
import { handleRadioGroupArrowKeys } from '../../../shared/utils';

export interface DemoDataFormState {
  /** True = load `variation`; false = start with an empty workspace. */
  loadDemo: boolean;
  variation: string | null;
}

interface DemoDataStepProps {
  industry: string;
  value: DemoDataFormState;
  onChange: (patch: Partial<DemoDataFormState>) => void;
}

const SKIP_KEY = '__skip__';

export function DemoDataStep({ industry, value, onChange }: DemoDataStepProps) {
  const { t } = useTranslation(['user_management', 'common']);
  const [variations, setVariations] = useState<SetupDemoVariation[]>([]);
  const [recommended, setRecommended] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  // Fetch the catalog once, keyed on the stated industry, and pre-select the
  // recommended variation (only if it's available; else stay on "start empty").
  useEffect(() => {
    let cancelled = false;
    setupWizardApi
      .getDemoCatalog(industry || null)
      .then((catalog) => {
        if (cancelled) return;
        setVariations(catalog.variations);
        setRecommended(catalog.recommended_variation);
        const rec = catalog.variations.find((v) => v.key === catalog.recommended_variation);
        if (rec?.available) {
          onChange({ loadDemo: true, variation: rec.key });
        }
        setLoaded(true);
      })
      .catch(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
    // Fetch once on mount; industry is stable for the wizard's lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recommended first, then the rest in config order.
  const ordered = useMemo(() => {
    if (!recommended) return variations;
    const rec = variations.filter((v) => v.key === recommended);
    const others = variations.filter((v) => v.key !== recommended);
    return [...rec, ...others];
  }, [variations, recommended]);

  const selectedKey = value.loadDemo ? value.variation : SKIP_KEY;

  const selectVariation = (v: SetupDemoVariation) => {
    if (!v.available) return;
    onChange({ loadDemo: true, variation: v.key });
  };

  return (
    <div className="space-y-5">
      <p className="text-sm text-muted-foreground">{t('setupWizardV2.demoData.subtitle')}</p>

      <div
        className="space-y-3"
        role="radiogroup"
        aria-label={t('setupWizardV2.demoData.heading')}
        onKeyDown={handleRadioGroupArrowKeys}
      >
        {ordered.map((v) => {
          const isRecommended = v.key === recommended;
          const isSelected = selectedKey === v.key;
          return (
            <VariationCard
              key={v.key}
              title={v.name}
              description={v.description}
              selected={isSelected}
              disabled={!v.available}
              badge={
                !v.available ? (
                  <Badge variant="secondary">{t('setupWizardV2.demoData.comingSoon')}</Badge>
                ) : isRecommended ? (
                  <Badge className="gap-1">
                    <Sparkles className="h-3 w-3" />
                    {industry
                      ? t('setupWizardV2.demoData.recommendedFor', { industry })
                      : t('setupWizardV2.demoData.recommended')}
                  </Badge>
                ) : null
              }
              onSelect={() => selectVariation(v)}
            />
          );
        })}

        {/* Always-available "start empty" option. */}
        {loaded && (
          <VariationCard
            title={t('setupWizardV2.demoData.skipTitle')}
            description={t('setupWizardV2.demoData.skipHint')}
            selected={selectedKey === SKIP_KEY}
            disabled={false}
            icon={<PackageOpen className="h-4 w-4 text-muted-foreground" />}
            onSelect={() => onChange({ loadDemo: false, variation: null })}
          />
        )}
      </div>
    </div>
  );
}

function VariationCard({
  title,
  description,
  selected,
  disabled,
  badge,
  icon,
  onSelect,
}: {
  title: string;
  description: string;
  selected: boolean;
  disabled: boolean;
  badge?: React.ReactNode;
  icon?: React.ReactNode;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      disabled={disabled}
      onClick={onSelect}
      className={cn(
        'flex w-full items-start gap-3 rounded-lg border p-4 text-start transition-colors',
        selected
          ? 'border-primary bg-primary/[0.04] ring-1 ring-primary'
          : 'border-border hover:bg-accent',
        disabled && 'cursor-not-allowed opacity-60 hover:bg-transparent',
      )}
    >
      <div
        className={cn(
          'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border',
          selected ? 'border-primary bg-primary text-primary-foreground' : 'border-muted-foreground/40',
        )}
      >
        {selected && <Check className="h-3 w-3" />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          {icon}
          <span className="font-medium">{title}</span>
          {badge}
        </div>
        <p className="mt-1 text-sm text-muted-foreground">{description}</p>
      </div>
    </button>
  );
}
